import {
    createContext,
    useContext,
    useState,
    useCallback,
    useEffect,
    useLayoutEffect,
    useRef,
} from 'react';
import { useHistory } from 'react-router-dom';
import { UnsavedChangesModal } from '../components/unsaved-changes-modal';

type UnsavedChangesContextType = {
    setDirty: (dirty: boolean) => void;
    setSaveHandler: (handler: (() => Promise<void>) | null) => void;
    checkNavigation: (navigate: () => void) => void;
    selectedMenuKey: string;
    setSelectedMenuKey: (key: string) => void;
};

const UnsavedChangesContext = createContext<UnsavedChangesContextType | null>(
    null
);

export const UnsavedChangesProvider = ({
    children,
}: {
    children: React.ReactNode;
}) => {
    const history = useHistory();

    const isDirtyRef = useRef(false);
    const saveHandlerRef = useRef<(() => Promise<void>) | null>(null);
    const pendingNavigateRef = useRef<(() => void) | null>(null);
    const saveFailCountRef = useRef(0);

    const [showModal, setShowModal] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [saveDisabled, setSaveDisabled] = useState(false);
    const [selectedMenuKey, setSelectedMenuKeyState] = useState(
        'users-analytics'
    );

    const setSelectedMenuKey = useCallback((key: string) => {
        setSelectedMenuKeyState(key);
    }, []);

    const setDirty = useCallback((dirty: boolean) => {
        isDirtyRef.current = dirty;
    }, []);

    const setSaveHandler = useCallback(
        (handler: (() => Promise<void>) | null) => {
            saveHandlerRef.current = handler;
        },
        []
    );

    const checkNavigation = useCallback((navigate: () => void) => {
        if (!isDirtyRef.current) {
            navigate();
            return;
        }
        pendingNavigateRef.current = navigate;
        // Disable save button only after 2 failed attempts
        setSaveDisabled(saveFailCountRef.current >= 2);
        setShowModal(true);
    }, []);

    useEffect(() => {
        const unblock = history.block((location) => {
            if (!isDirtyRef.current) return;
            // Store the pending navigation so we can execute it after save
            pendingNavigateRef.current = () => {
                history.push(
                    location.pathname + location.search + location.hash
                );
            };
            // Disable save button only after 2 failed attempts
            setSaveDisabled(saveFailCountRef.current >= 2);
            setShowModal(true);
            return false;
        });
        return () => unblock();
    }, [history]);

    useEffect(() => {
        const handleBeforeUnload = (e: BeforeUnloadEvent) => {
            if (!isDirtyRef.current) return;
            e.preventDefault();
            e.returnValue = '';
        };

        window.addEventListener('beforeunload', handleBeforeUnload);
        return () =>
            window.removeEventListener('beforeunload', handleBeforeUnload);
    }, []);

    const handleSave = useCallback(async () => {
        if (!saveHandlerRef.current) return;

        setIsSaving(true);
        try {
            await saveHandlerRef.current();
            // Clear dirty state before navigating to prevent re-blocking
            isDirtyRef.current = false;
            saveFailCountRef.current = 0;
            setShowModal(false);
            pendingNavigateRef.current?.();
            pendingNavigateRef.current = null;
        } catch {
            saveFailCountRef.current++;
            if (saveFailCountRef.current >= 2) {
                // Second failure: disable save button
                setSaveDisabled(true);
            } else {
                // First failure: close modal, user can try again
                setShowModal(false);
                pendingNavigateRef.current = null;
            }
        } finally {
            setIsSaving(false);
        }
    }, []);

    const handleDiscard = useCallback(() => {
        // Clear dirty state and navigate without saving
        isDirtyRef.current = false;
        saveFailCountRef.current = 0;
        setShowModal(false);
        setSaveDisabled(false);
        pendingNavigateRef.current?.();
        pendingNavigateRef.current = null;
    }, []);

    return (
        <UnsavedChangesContext.Provider
            value={{
                setDirty,
                setSaveHandler,
                checkNavigation,
                selectedMenuKey,
                setSelectedMenuKey,
            }}
        >
            {children}
            {showModal && (
                <UnsavedChangesModal
                    onSave={handleSave}
                    onDiscard={handleDiscard}
                    onClose={() => setShowModal(false)}
                    isSaving={isSaving}
                    saveDisabled={saveDisabled}
                />
            )}
        </UnsavedChangesContext.Provider>
    );
};

type UseUnsavedChangesOptions = {
    dirty: boolean;
    onSave: () => Promise<void>;
};

/**
 * Hook for form pages to enable unsaved changes protection.
 *
 * @param options.dirty - Pass formik.dirty or similar boolean
 * @param options.onSave - Async save function called when user clicks "Save" in modal
 *
 * @example
 * const { clearDirty } = useUnsavedChanges({
 *     dirty: formik.dirty,
 *     onSave: handleSave,
 * });
 *
 * // In save handler - clear dirty and navigate:
 * clearDirty(() => navigation.goToList());
 *
 * // Or just clear dirty (stay on page):
 * clearDirty();
 */
export const useUnsavedChanges = (options: UseUnsavedChangesOptions) => {
    const { dirty, onSave } = options;

    const context = useContext(UnsavedChangesContext);
    if (!context) {
        throw new Error(
            'useUnsavedChanges must be used within UnsavedChangesProvider'
        );
    }

    useLayoutEffect(() => {
        context.setDirty(dirty);
        return () => context.setDirty(false);
    }, [dirty, context]);

    useEffect(() => {
        context.setSaveHandler(onSave);
    }, [onSave, context]);

    const clearDirty = useCallback(
        (navigate?: () => void) => {
            context.setDirty(false);
            navigate?.();
        },
        [context]
    );

    return { clearDirty };
};

/**
 * Hook for navigation components (e.g., menus) to check before navigating.
 * Manages selectedMenuKey state and restores it on cancel.
 *
 * @example
 * const { checkNavigation, selectedMenuKey, setSelectedMenuKey } = useCheckNavigation();
 *
 * setSelectedMenuKey(menuKey);
 * checkNavigation(() => navigation.goToPage());
 */
export const useCheckNavigation = () => {
    const context = useContext(UnsavedChangesContext);
    if (!context) {
        throw new Error(
            'useCheckNavigation must be used within UnsavedChangesProvider'
        );
    }
    return {
        checkNavigation: context.checkNavigation,
        selectedMenuKey: context.selectedMenuKey,
        setSelectedMenuKey: context.setSelectedMenuKey,
    };
};
